import scrypt from "scrypt-js";
import buffer from "scrypt-js/thirdparty/buffer";
import aes from "crypto-js/aes";
import encUtf8 from "crypto-js/enc-utf8";
import "scrypt-js/thirdparty/setImmediate";
import "scrypt-js/thirdparty/unorm";

/**
 * Creates hash from a string and salt by scrypt algorythm.
 *
 * @param   {String} secret - String to be hashed.
 * @param   {String} salt - Salt to enforce hash.
 * @returns {Promise} Promise that returns resuling hash.
 *
 * @example
 *   createScryptHash("mySuperSecretKey", "mySuperSalt")
 *    .then(hash => console.log(hash));
 */
export function createScryptHash(secret, salt) {
  const options = {
    N: 16384,
    r: 8,
    p: 1,
    dkLen: 32,
  };

  const bufferSecret = new buffer.SlowBuffer(secret.normalize("NFKC"));
  const bufferSalt = new buffer.SlowBuffer(salt.normalize("NFKC"));

  return new Promise(resolve => {
    scrypt(
      bufferSecret,
      bufferSalt,
      options.N,
      options.r,
      options.p,
      options.dkLen,
      (error, progress, key) => {
        if (error) {
          throw error;
        } else if (key) {
          const base64 = Buffer.from(key).toString("base64");
          return resolve(base64);
        }
      },
    );
  });
}

/**
 * Encrypts string by AES algorythm.
 *
 * @param   {String} secret - String to encrypted.
 * @param   {String} aesKey - Aeskey to use to encrypt secret.
 * @returns {String} Encrypted string with provided aeskey.
 */
export function aesEncrypt(secret, aesKey) {
  return aes.encrypt(secret, aesKey).toString();
}

/**
 * Decrypts encrypted string by AES algorythm.
 *
 * @param   {String} encryptedSecret - Encrypted string.
 * @param   {String} aesKey - Aeskey to use to decrypt secret.
 * @returns {String} Original decrypted string.
 */
export function aesDecrypt(encryptedSecret, aesKey) {
  try {
    return aes.decrypt(encryptedSecret, aesKey).toString(encUtf8);
  } catch (err) {
    throw new Error("Can't decrypt provided message");
  }
}
